import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { formatDate } from "../../api";
import { useApi } from "../../hooks";
import { EmptyState, ErrorState, LoadingState } from "../Ui";
import type { BrightLocalClient } from "../BrightLocalSetup";

// One empty mark for the whole table: "—" (no rank, no volume), as everywhere else in the app.
const rank = (n: number | null) => (n == null ? "—" : n > 20 ? "20+" : String(n));
/** Positions gained since the last run (up is positive). Past 20 counts as 21; no rank either time is no change shown. */
export function rankMove(now: number | null, before: number | null | undefined): number | null {
  if (before === undefined || (now == null && before == null)) return null;
  const pos = (n: number | null) => (n == null || n > 20 ? 21 : n);
  return pos(before) - pos(now);
}
/** Keeps an empty slot when there is no move, so ranks stay lined up down the column. */
function Move({ now, before }: { now: number | null; before: number | null | undefined }) {
  const move = rankMove(now, before);
  if (!move) return <span className="kw-move" aria-hidden="true" />;
  return <span className={`kw-move ${move > 0 ? "up" : "down"}`} aria-label={`${move > 0 ? "up" : "down"} ${Math.abs(move)} since the last run`}>{move > 0 ? "▲" : "▼"}{Math.abs(move)}</span>;
}
/** A report link only when it is http(s); BrightLocal's own URLs always are. */
const reportUrl = (value: string | null | undefined) => (value && /^https?:\/\//i.test(value) ? value : null);
const PART_NOTE: Record<string, string> = {
  not_entitled: "Rank tracking isn't on the BrightLocal plan.",
  no_report: "No Rank Tracker report is set up in BrightLocal for this location.",
  not_found: "The Rank Tracker report wasn't found in BrightLocal.",
  rate_limited: "BrightLocal is rate limiting. Try again shortly.",
};

/**
 * Where the client ranks for its keywords, from BrightLocal's Rank Tracker: the
 * same figures the Reviews page's BrightLocal panel summarises, in full here
 * beside the keyword research. Read only; BrightLocal's daily sync fills it.
 */
export function RankingsTab({ clientId, isAdmin }: { clientId: number; isAdmin: boolean }) {
  const bl = useApi<BrightLocalClient>(`/api/brightlocal/clients/${clientId}`);
  if (bl.loading) return <LoadingState label="Loading rankings" />;
  if (bl.error || !bl.data) return <ErrorState message={bl.error || "Rankings could not load."} onRetry={bl.reload} />;
  const links = bl.data.links;
  if (!links.length) return (
    <section className="panel">
      <EmptyState
        title="No rankings yet"
        description="Keyword rankings come from BrightLocal's Rank Tracker once this client's location is linked."
        action={isAdmin ? <Link className="button secondary" to={`/client-settings?client_id=${clientId}&section=brightlocal`}>Link BrightLocal</Link> : undefined}
      />
    </section>
  );
  return (
    <>
      {links.map((l) => {
        const r = l.summary?.rankings;
        const kws = r?.keywords || [];
        const url = reportUrl(r?.url);
        const prev = l.previous_ranks;
        const before = (keyword: string) => prev?.keywords[keyword.trim().toLowerCase()];
        return (
          <section key={l.bl_location_id} className="panel kw-ranks">
            <div className="panel-heading">
              <div>
                <h2>{links.length > 1 ? l.location_name : "Keyword rankings"}</h2>
                <p>
                  {r?.status === "ok" && kws.length ? `${kws.length.toLocaleString()} ${kws.length === 1 ? "keyword" : "keywords"} tracked in BrightLocal` : "BrightLocal Rank Tracker"}
                  {l.last_synced_at ? `, synced ${formatDate(l.last_synced_at)}` : ""}
                  {prev ? `. Arrows show the change since the ${formatDate(prev.processed_on)} run.` : ""}
                </p>
              </div>
              {url && <a className="button secondary" href={url} target="_blank" rel="noreferrer"><ExternalLink aria-hidden="true" />Rank Tracker report</a>}
            </div>
            {r?.status === "ok" && kws.length ? (
              <div className="table-wrap">
                <table className="kw-table">
                  <thead><tr>
                    <th>Keyword</th>
                    <th className="num">Google</th>
                    <th className="num">Maps</th>
                    <th className="num">Mobile</th>
                    <th className="num" title="Monthly searches, from BrightLocal">Searches / mo</th>
                  </tr></thead>
                  <tbody>
                    {kws.map((k) => (
                      <tr key={k.keyword}>
                        <td data-label="Keyword"><strong>{k.keyword}</strong></td>
                        <td className="number-cell" data-label="Google">{rank(k.google)}{prev && <Move now={k.google} before={before(k.keyword)?.google} />}</td>
                        <td className="number-cell" data-label="Maps">{rank(k.maps)}{prev && <Move now={k.maps} before={before(k.keyword)?.maps} />}</td>
                        <td className="number-cell" data-label="Mobile">{rank(k.mobile)}{prev && <Move now={k.mobile} before={before(k.keyword)?.mobile} />}</td>
                        <td className="number-cell" data-label="Searches / mo">{k.volume != null ? k.volume.toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted kw-empty">
                {PART_NOTE[r?.status || ""] || r?.message || (r?.status === "ok" ? "No keywords are tracked in BrightLocal for this location." : "Rankings arrive with the next BrightLocal sync.")}
              </p>
            )}
          </section>
        );
      })}
    </>
  );
}
